import { CommonModule } from '@angular/common';
import { Component, effect, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { TextareaModule } from 'primeng/textarea';
import { TooltipModule } from 'primeng/tooltip';

// TODO: Ajustar imports para apontar para libs do monorepo
// import { PwaOnlineStatusService } from '@nomoh-monorepo/shared-pwa-service';
// import { NotificacaoService } from '@nomoh-monorepo/data-access-shared-notificacao';
// import { formataDataHora, isSameDay } from '@nomoh-monorepo/shared-util-tools';
// import { getStatusAgendamentoPorValor, STATUS_AGENDAMENTO } from '@nomoh-monorepo/shared-constants';

import {
  AgendaDataService,
  EstabelecimentoDataService,
  NichoTermosService,
} from '@nomoh-monorepo/data-access-bardo-administrador';

import {
  IAgendamento,
  StatusAgendamentoEnum,
} from '@nomoh-monorepo/shared-types';
import { formatarTelefone } from '@nomoh-monorepo/shared-util-tools';
import { DateTime } from 'luxon';
import { TranslateModule } from '@ngx-translate/core';
import { AgendaModalInformacaoAgendamentoComponent } from '../agenda-modal-informacao-agendamento/agenda-modal-informacao-agendamento.component';

// TODO: Importar componentes quando forem criados
// import { AgendaModalInformacaoAgendamentoComponent } from '../agenda-modal-informacao-agendamento/agenda-modal-informacao-agendamento.component';
// import { AgendaNovoAgendamentoComponent } from '../agenda-novoAgendamento/agenda-novoAgendamento.component';

// TODO: Service que precisa ser copiado
// import { AgendaListaAgendamentoService } from './service/agenda-listaAgendamento.service';

@Component({
  selector: 'lib-agenda-lista-agendamento',
  standalone: true,
  imports: [
    CommonModule,
    DialogModule,
    ConfirmPopupModule,
    DividerModule,
    FormsModule,
    ButtonModule,
    TextareaModule,
    TooltipModule,
    AgendaModalInformacaoAgendamentoComponent,
    TranslateModule,
    // AgendaNovoAgendamentoComponent,
  ],
  templateUrl: './agenda-lista-agendamento.component.html',
  styleUrl: './agenda-lista-agendamento.component.scss',
})
export class AgendaListaAgendamentoComponent {
  private readonly timeZonePadrao = 'America/Sao_Paulo';

  //#region Injeção de Dependências
  AgendaDataService = inject(AgendaDataService);
  private estabelecimentoDataService = inject(EstabelecimentoDataService);
  $nicho = inject(NichoTermosService).$nicho;
  confirmationService = inject(ConfirmationService);


  //#region signals
  $modalIsVisible = signal(false);
  //#endregion

  //#region propriedades
  private readonly statusClassMap: Record<number, string> = {
    [StatusAgendamentoEnum.CANCELADO]: 'n-status-canceled',
    [StatusAgendamentoEnum.CONFIRMADO]: 'n-status-approved',
    [StatusAgendamentoEnum.PENDENTE]: 'n-status-pending',
    [StatusAgendamentoEnum.REALIZADO]: 'n-status-realized',
  };
  agendamento: IAgendamento = {} as IAgendamento;
  //#endregion

  // TODO: Descomentar quando utilities estiverem disponíveis
  constructor() {
    effect(() => this.sincronizarAgendamentosPorData());
  }

  obterClasseStatus(agendamento: IAgendamento): string {
    return (
      this.statusClassMap[agendamento.status ?? StatusAgendamentoEnum.PENDENTE] ??
      'n-status-pending'
    );
  }

  obterNomeCliente(agendamento: IAgendamento): string {
    const extendido = agendamento as AgendamentoExtendido;
    return (
      extendido.cliente?.nome?.trim() ||
      extendido.nomeCliente?.trim() ||
      extendido.pessoa?.nome?.trim() ||
      ''
    );
  }

  obterTelefoneCliente(agendamento: IAgendamento): string {
    const extendido = agendamento as AgendamentoExtendido;
    return (
      extendido.cliente?.telefone?.trim() ||
      extendido.telefoneCliente?.trim() ||
      extendido.pessoa?.telefone?.trim() ||
      ''
    );
  }

  obterTelefoneResponsavel(agendamento: IAgendamento): string {
    const extendido = agendamento as AgendamentoExtendido;
    return (
      extendido.responsavelTelefone?.trim() ||
      extendido.cliente?.responsavelTelefone?.trim() ||
      ''
    );
  }

  formatarTelefoneValor(telefone: string): string {
    return formatarTelefone(telefone);
  }

  formatarHoraAgendamento(agendamento: IAgendamento): string {
    return this.toInstantDateTime(agendamento.dataHora)?.toFormat('HH:mm') ?? '';
  }

  formatarDataAgendamento(agendamento: IAgendamento): string {
    return this.toInstantDateTime(agendamento.dataHora)?.toFormat('dd/MM') ?? '';
  }

  acessarModalInformacao(agendamento: IAgendamento) {
    this.$modalIsVisible.set(true);
    this.agendamento = agendamento;
  }

  private sincronizarAgendamentosPorData(): void {
    const agenda = this.AgendaDataService.$agenda();
    const dataSelecionada = this.AgendaDataService.$dataSelecionada();

    if (!agenda || !dataSelecionada) {
      this.AgendaDataService.setAgendamentos([]);
      return;
    }

    const agendamentosFiltrados =
      agenda.agendamentos?.filter((agendamento) =>
        this.isSameDay(agendamento.dataHora, dataSelecionada)
      ) ?? [];

    if (agendamentosFiltrados.length > 0) {
      this.AgendaDataService.setAgendamentos(agendamentosFiltrados);
    } else {
      this.AgendaDataService.setAgendamentos([]);
    }
  }

  private isSameDay(
    primeiro: Date | string | null | undefined,
    segundo: Date | string | null | undefined
  ): boolean {
    const primeiraData = this.toInstantDateTime(primeiro);
    const segundaData = this.toCalendarDateTime(segundo);

    if (!primeiraData || !segundaData) {
      return false;
    }

    return primeiraData.hasSame(segundaData, 'day');
  }

  private toInstantDateTime(
    valor: Date | string | null | undefined
  ): DateTime | null {
    if (!valor) {
      return null;
    }

    const dateTime =
      valor instanceof Date
        ? DateTime.fromJSDate(valor)
        : DateTime.fromISO(valor);
    return dateTime.isValid
      ? dateTime.setZone(this.getTimeZoneEstabelecimento())
      : null;
  }

  private toCalendarDateTime(
    valor: Date | string | null | undefined
  ): DateTime | null {
    if (!valor) {
      return null;
    }

    if (valor instanceof Date) {
      return DateTime.fromObject(
        {
          year: valor.getFullYear(),
          month: valor.getMonth() + 1,
          day: valor.getDate(),
        },
        { zone: this.getTimeZoneEstabelecimento() }
      );
    }

    const dateTime = DateTime.fromISO(valor, {
      zone: this.getTimeZoneEstabelecimento(),
    });
    return dateTime.isValid ? dateTime : null;
  }

  private getTimeZoneEstabelecimento(): string {
    const timeZone =
      this.estabelecimentoDataService
        .$estabelecimentoAtual()
        ?.timeZone?.trim() || this.timeZonePadrao;
    return DateTime.now().setZone(timeZone).isValid
      ? timeZone
      : this.timeZonePadrao;
  }
}

type AgendamentoExtendido = IAgendamento & {
  pessoa?: { nome?: string; telefone?: string };
  cliente?: { nome?: string; telefone?: string; responsavelTelefone?: string };
};
